import React, { useState, useEffect } from "react";
import { useI18n } from "../../i18n";
import api from "../../api";
import MethodologyDisclaimer from "../MethodologyDisclaimer";
import ShoppingListModal from "../ShoppingListModal";

const Overview = ({ profile, calc, weeklyData }) => {
  const { t, lang } = useI18n();
  const [hydration, setHydration] = useState(null);
  const [shoppingOpen, setShoppingOpen] = useState(false);

  useEffect(() => {
    api.post("/plan/hydration", profile, {
      params: { sweat_level: 5, day_type: "hard_training" },
    }).then((r) => setHydration(r.data));
  }, [profile]);

  // Real weekly average is more honest than the static TDEE-of-today number
  // for athletes whose week mixes hard / light / rest / match days. We surface
  // the average as the headline number and keep the theoretical TDEE as the
  // secondary line so the user understands the relationship between the two.
  const weekTotalKcal = weeklyData?.weekly_plan?.week_totals?.kcal;
  const weeklyAvgKcal = weekTotalKcal ? Math.round(weekTotalKcal / 7) : null;
  const tdeeTheoretical = calc.tdee.tdee;
  // Headline shows the average when we have it, otherwise falls back to TDEE.
  const headlineKcal = weeklyAvgKcal ?? tdeeTheoretical;

  return (
    <div>
    <div className="grid md:grid-cols-3 gap-4 md:gap-6">
      <div className="sne-card md:col-span-2">
        <div className="font-mono text-xs tracking-widest text-white/50 mb-2">
          {weeklyAvgKcal
            ? (lang === "ro" ? "MEDIA REALĂ / 7 ZILE" : "REAL 7-DAY AVERAGE")
            : `${t.dashboard.tdee} · ${t.dashboard.today}`}
        </div>
        <div className="flex items-baseline gap-4">
          <div className="stat-num text-7xl md:text-8xl" data-testid="overview-tdee">{headlineKcal}</div>
          <div className="font-mono text-sm text-white/70">{t.dashboard.kcal}</div>
        </div>
        {weeklyAvgKcal ? (
          <div
            className="mt-4 font-mono text-xs text-white/50"
            data-testid="overview-tdee-breakdown"
          >
            {lang === "ro" ? "TDEE teoretic AZI" : "Theoretical TDEE today"}: {tdeeTheoretical} kcal
            <span className="text-white/30"> · </span>
            {lang === "ro" ? "total săptămână" : "week total"}: {weekTotalKcal} kcal
            <div className="mt-1 text-white/30">
              BMR {calc.tdee.bmr} · ×{calc.tdee.activity_factor} · +{calc.tdee.sport_kcal} sport · {calc.tdee.goal_adjustment >= 0 ? "+" : ""}{calc.tdee.goal_adjustment} goal
            </div>
          </div>
        ) : (
          <div className="mt-4 font-mono text-xs text-white/40">
            BMR {calc.tdee.bmr} · ×{calc.tdee.activity_factor} · +{calc.tdee.sport_kcal} sport · {calc.tdee.goal_adjustment >= 0 ? "+" : ""}{calc.tdee.goal_adjustment} goal
          </div>
        )}
      </div>

      <div className="sne-card">
        <div className="font-mono text-xs tracking-widest text-white/50 mb-3">{t.dashboard.hydration}</div>
        <div className="stat-num text-5xl">{hydration?.total_liters || "—"}<span className="text-lg ml-1">L</span></div>
        <div className="mt-2 font-mono text-xs text-white/40">
          {hydration?.extra_sodium_mg ? `+${hydration.extra_sodium_mg} mg Na` : ""}
        </div>
      </div>

      <MacroCard
        label={t.dashboard.protein} grams={calc.macros.protein_g} kcal={calc.macros.protein_kcal}
        color="#FF3B30" testid="overview-protein"
        pct={Math.round((calc.macros.protein_kcal / calc.macros.total_kcal) * 100)}
      />
      <MacroCard
        label={t.dashboard.carbs} grams={calc.macros.carbs_g} kcal={calc.macros.carbs_kcal}
        color="#007AFF" testid="overview-carbs"
        pct={Math.round((calc.macros.carbs_kcal / calc.macros.total_kcal) * 100)}
      />
      <MacroCard
        label={t.dashboard.fat} grams={calc.macros.fat_g} kcal={calc.macros.fat_kcal}
        color="#F59E0B" testid="overview-fat"
        pct={Math.round((calc.macros.fat_kcal / calc.macros.total_kcal) * 100)}
      />
    </div>

    {/* Weekly shopping list — aggregates all foods from the 7-day plan into
        practical units (12 banane, 1.8 kg pui, 8 felii pâine). One-click PDF
        download so the user / parent can take a real grocery list to the
        store instead of weighing every ingredient.                          */}
    <div className="mt-6 sne-card flex items-center justify-between flex-wrap gap-4">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#FF3B30] mb-1">
          {lang === "ro" ? "LISTĂ CUMPĂRĂTURI" : "SHOPPING LIST"}
        </div>
        <div className="font-heading text-xl mb-1">
          {lang === "ro" ? "Toate alimentele săptămânii" : "Whole week, one list"}
        </div>
        <div className="text-sm text-white/60">
          {lang === "ro"
            ? "Cantități practice: 12 banane, 1.8 kg pui, 8 felii pâine. Adio cântar la magazin."
            : "Practical units: 12 bananas, 1.8 kg chicken, 8 bread slices. No scale needed."}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShoppingOpen(true)}
        data-testid="open-shopping-list-btn"
        className="px-4 py-2 font-mono text-xs tracking-widest"
        style={{ background: "#FF3B30", color: "#fff", border: "none", cursor: "pointer" }}
      >
        {lang === "ro" ? "VEZI LISTA" : "VIEW LIST"}
      </button>
    </div>

    <MethodologyDisclaimer />

    {shoppingOpen && (
      <ShoppingListModal
        profile={profile}
        onClose={() => setShoppingOpen(false)}
      />
    )}
    </div>
  );
};

const MacroCard = ({ label, grams, kcal, color, pct, testid }) => (
  <div className="sne-card" data-testid={testid}>
    <div className="font-mono text-xs tracking-widest text-white/50 mb-2">{label}</div>
    <div className="flex items-baseline gap-2">
      <span className="stat-num text-5xl">{grams}</span>
      <span className="font-mono text-sm text-white/60">g</span>
    </div>
    <div className="mt-3 progress-track">
      <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
    <div className="mt-2 font-mono text-xs text-white/40">{kcal} kcal · {pct}%</div>
  </div>
);

export default Overview;
